import StatusTag from '../components/Chat/StatusTag.tsx';
import ChatFilterControl, { ChatFilterChips } from '../components/Chat/ChatFilterControl.tsx';
import type { ContactFilters } from '../components/ContactFilters.tsx';
import { extractProviderMessageId } from '../utils/sentMessage.ts';
import ContactAttendance, { ConversationTabs, type AssignmentFilter, useAttendanceEnabled, type ConversationState } from '../components/Chat/ContactAttendance.tsx';
// ChatMobile.tsx - Versão otimizada para dispositivos móveis
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Search, Send, ArrowLeft, Mic, ImageIcon, VideoIcon,
  Calendar, Target, Check, CheckCheck, Clock,
  CheckCircle2, DollarSign, Menu, MoreVertical, Loader2
} from 'lucide-react';
import api, {
  getContacts,
  getContactMode,
  markContactAsRead,
  sendWhatsAppText,
  sendWhatsAppAudio,
  sendWhatsAppImage,
  sendWhatsAppVideo,
  sendNPS,
  getClinicInfo,
  unifiedWebSocketManager,
  OptimizedMessage,
  formatBrazilianPhone,
  parseApiError,
  type ContactModeResponse,
  type ParsedApiError
} from '../services/api.ts';
import { formatChatTimestamp } from '../utils/date.ts';
import { useOptimizedMessages } from '../hooks/useOptimizedMessages.tsx';
import { useInstagramConversation } from '../hooks/useInstagramConversation.ts';
import { useInstagramComments } from '../hooks/useInstagramComments.ts';
import { markInstagramRead } from '../services/instagram.ts';
import { conversationEventKey, conversationKey } from '../utils/conversationIdentity.ts';
import { formatLastMessage } from '../utils/lastMessagePreview.ts';
import { InstagramCommentComposer } from '../components/Chat/InstagramCommentComposer.tsx';
import ChatChannelBadge from '../components/Chat/ChannelBadge.tsx';
import DeliveryFailureHint from '../components/Chat/DeliveryFailureHint.tsx';
import { resolveContactDisplayChannel } from '../utils/contactDisplayChannel.ts';
import {
  ContactMessageDeliveryInbox, contactDeliveryScope,
  mergeContactMessageDelivery, preserveCurrentContactMessage,
} from '../utils/contactMessageDelivery.ts';
import type { LastMessageDelivery } from '../utils/contactMessageDelivery.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { VirtualizedMessageList } from '../components/VirtualizedMessageList.tsx';
import { OptimizedMessageInput } from '../components/OptimizedMessageInput.tsx';
import {
  ContactPresenceBubble,
  ContactPresenceStatusLine,
} from '../components/Chat/ContactPresence.tsx';
import TaskPanelMobile from '../components/TaskPanelMobile.tsx';
import WhatsAppIdentityErrorAlert from '../components/WhatsAppIdentityErrorAlert.tsx';
import {
  isChatContactIdentityResolved,
  pickNavigationContact,
  readChatNavigationPayload,
  type ChatNavigationPayload,
  type ChatNavigationState,
} from '../components/Chat/chatNavigationIdentity.ts';
import ContactModeControl, {
  applyContactModeToCollection,
  classifyContactModeEvent,
  registerContactModeVersion,
  resolvePolledContactMode,
  withKnownContactMode,
} from '../components/Chat/ContactModeControl.tsx';
import type { ContactModeVersion } from '../components/Chat/ContactModeControl.tsx';
import { useContactPresence } from '../hooks/useContactPresence.ts';
import { hasPermission } from '../services/permissionService.ts';
import { activeClinicId } from '../services/agentLearning.ts';
import { AgentCorrectionDialog } from '../components/AgentLearning/AgentCorrectionDialog.tsx';

// Interface simplificada para mobile
interface Contact extends LastMessageDelivery {
  id: number;
  phone: string;
  active_channel?: string;
  source_channel?: string;
  instagram_identity_id?: number | null;
  instagram_username?: string | null;
  lastMessageType?: string | null;
  name: string;
  photo: string;
  lastMessage?: string;
  timestamp?: string;
  timestampNumber: number;
  unreadCount: number;
  human_mode?: boolean;
  archived?: boolean;
  possible_duplicate?: boolean;
  duplicate_contact_id?: number | null;
  funnel_stage?: 'contato' | 'lead' | 'agendado' | 'compareceu' | 'faltou' | 'venda' | 'paciente' | 'perdido';
  source_id?: string;
  // Campos de tarefas
  pending_tasks_count?: number;
  next_task?: {
    id: number;
    title: string;
    scheduled_for: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
  };
}

const ChatMobile: React.FC = () => {
  const { isDark } = useTheme();
  const attendanceClaimTarget = React.useRef<HTMLDivElement>(null);
  const attendanceCloseTarget = React.useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  // Estados - declarados primeiro
  const attendanceEnabled = useAttendanceEnabled();
  const [conversationState, setConversationState] = useState<ConversationState>('open');
  const [attendanceRevision, setAttendanceRevision] = useState(0);
  const [chatFilters, setChatFilters] = useState<ContactFilters>({ funnelStages: [], activeFlows: [] });
  const [assignmentFilter, setAssignmentFilter] = useState<AssignmentFilter>('all');
  const [contacts, setContacts] = useState<Map<string, Contact>>(new Map());
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const selectedContactLive = React.useRef(selectedContact);
  selectedContactLive.current = selectedContact;
  const instagramAudioFiles = React.useRef(new WeakMap<Blob, File>());
  const selectionGeneration = React.useRef(0);

  useEffect(() => {
    if (!attendanceEnabled) return;
    return unifiedWebSocketManager.onMessage('*', (event: any) => {
      if ((event.type || event.data?.type) !== 'contact_attendance_invalidated') return;
      contactsRef.current = new Map();
      setPhoneSearchResults([]);
      setContacts(new Map());
      setAttendanceRevision(value => value + 1);
      window.dispatchEvent(new Event('contact-attendance-changed'));
      if (selectedContact?.id) {
        const id = selectedContact.id;
        api.get(`/api/contacts/${id}/attendance`, { params: { clinic_id: localStorage.getItem('clinic_id') } }).then(response => {
          if (response.data.enabled) setSelectedContact(current => current?.id === id ? { ...current, human_mode: response.data.human_mode === true } : current);
        }).catch((error: any) => {
          if ([403, 404].includes(error.response?.status)) setSelectedContact(current => current?.id === id ? null : current);
        });
      }
    });
  }, [attendanceEnabled, selectedContact?.id]);
  const [newMessage, setNewMessage] = useState('');
  const [clinicInfo, setClinicInfo] = useState<{name: string; logo_url: string | null}>({
    name: 'Clínica',
    logo_url: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [phoneSearchResults, setPhoneSearchResults] = useState<Contact[]>([]);
  const [isPhoneSearchLoading, setIsPhoneSearchLoading] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  useEffect(() => { if (attendanceEnabled) setShowUnreadOnly(false); }, [attendanceEnabled]);
  const [currentView, setCurrentView] = useState<'list' | 'chat'>('list');
  const [showTaskPanel, setShowTaskPanel] = useState(false);
  const [showFlowControl, setShowFlowControl] = useState(false);
  const [contactFlowStatus, setContactFlowStatus] = useState<{[key: string]: any}>({});
  const [isLoadingFlowStatus, setIsLoadingFlowStatus] = useState(false);
  const [sendError, setSendError] = useState<ParsedApiError | null>(null);
  const [correctionMessage, setCorrectionMessage] = useState<OptimizedMessage | null>(null);
  const [correctionContactId, setCorrectionContactId] = useState<number | null>(null);
  const [correctionClinicId, setCorrectionClinicId] = useState<number | null>(null);
  const [correctionPublished, setCorrectionPublished] = useState(false);
  const [pendingChatNavigation, setPendingChatNavigation] = useState<ChatNavigationPayload | null>(null);
  const pendingRouteNavigationRef = React.useRef<ChatNavigationPayload | null>(null);
  const contactModeVersionRef = React.useRef<Map<number, ContactModeVersion>>(new Map());
  const contactModeValueRef = React.useRef<Map<number, boolean>>(new Map());
  const contactsRef = React.useRef<Map<string, Contact>>(new Map());
  const deliveryInboxRef = React.useRef(new ContactMessageDeliveryInbox());
  const contactModeReconcileGenerationRef = React.useRef<Map<number, number>>(new Map());
  const contactModeReconcileInFlightRef = React.useRef<Map<number, Promise<void>>>(new Map());
  const selectedContactId = Number(selectedContact?.id || 0);
  const incomingChatNavigation = readChatNavigationPayload(
    location.state as ChatNavigationState,
  );
  const incomingWhatsAppNavigation =
    incomingChatNavigation &&
    !incomingChatNavigation.isInstagram &&
    incomingChatNavigation.rawPhone
      ? incomingChatNavigation
      : null;
  const hasPendingContactNavigation =
    pendingChatNavigation !== null || incomingWhatsAppNavigation !== null;
  const isSelectedContactIdentityResolved = isChatContactIdentityResolved({
    selectedContactId,
    hasPendingNavigation: hasPendingContactNavigation,
    archived: selectedContact?.archived === true,
    treatArchivedAsUnresolved: true,
  });

  const requireSelectedContactId = (): number | null => {
    if (!hasPendingContactNavigation && isSelectedContactIdentityResolved) {
      return selectedContactId;
    }

    const isArchived =
      selectedContact?.archived === true && !hasPendingContactNavigation;
    setSendError({
      code: isArchived ? 'archived_contact' : 'contact_identity_pending',
      message: isArchived
        ? 'Este cadastro está arquivado. Selecione o cadastro ativo antes de enviar.'
        : 'Este contato ainda não possui uma identificação segura. Revise os cadastros antes de enviar.',
      existingContactId: selectedContactId > 0 ? selectedContactId : undefined,
      existingPhone: selectedContact?.phone,
    });
    return null;
  };
  const numericSearchDigits = searchTerm.replace(/\D/g, '');
  const isNumericSearch =
    searchTerm.trim().length > 0 &&
    numericSearchDigits.length > 0 &&
    !/[A-Za-zÀ-ÿ]/.test(searchTerm);
  const shouldSearchPhoneBackend = isNumericSearch && numericSearchDigits.length >= 8;
  
  // Aplicar reset de CSS para mobile ao montar
  React.useEffect(() => {
    // Salvar overflow original
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    
    // Prevenir scroll no body
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    // Cleanup ao desmontar
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = '';
    };
  }, []);

  // Controlar visibilidade da navbar baseado na view
  React.useEffect(() => {
    const navbar = document.querySelector('nav.fixed.bottom-0');
    if (navbar) {
      if (currentView === 'chat') {
        navbar.classList.add('hidden');
      } else {
        navbar.classList.remove('hidden');
      }
    }
    
    return () => {
      // Garantir que a navbar volte a aparecer quando sair do componente
      const navbar = document.querySelector('nav.fixed.bottom-0');
      if (navbar) {
        navbar.classList.remove('hidden');
      }
    };
  }, [currentView]);
  
  // Refs removidos - não utilizados na versão simplificada

  // Funções auxiliares
  const normalizePhone = (phone: string): string => {
    if (!phone) return '';
    return phone.replace(/\D/g, '');
  };

  useEffect(() => {
    if (!selectedContact) return;
    const currentId = Number(selectedContact.id || 0);
    if (Number.isInteger(currentId) && currentId > 0) return;
    const phone = normalizePhone(selectedContact.phone);
    const fresh = contacts.get(phone);
    const freshId = Number(fresh?.id || 0);
    if (!Number.isInteger(freshId) || freshId <= 0) return;
    setSelectedContact((current) => (
      current && Number(current.id || 0) <= 0 ? { ...current, id: freshId, phone } : current
    ));
  }, [contacts, selectedContact]);

  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);

  // Hook de mensagens
  const {
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMoreMessages,
    updateMessageTracking,
    sendMessage
  } = useOptimizedMessages(
    conversationKey(selectedContact) || null,
    { pageSize: 30 }
  );

  const selectedIsInstagram = (selectedContact?.active_channel || selectedContact?.source_channel) === 'instagram';
  const instagramConversation = useInstagramConversation(selectedIsInstagram && isSelectedContactIdentityResolved ? selectedContactId : null);
  const instagramComments = useInstagramComments(selectedIsInstagram ? selectedContactId : null, instagramConversation.identity, result => {
    if (result.message_id && result.content) sendMessage(result.content, 'comment', String(result.message_id), 'instagram', {
      status: 'sent', providerMessageId: result.provider_message_id ? `ig-comment:${result.provider_message_id}` : null,
    });
  });
  const instagramReady = !!instagramConversation.identity && !instagramConversation.loading && !instagramConversation.sending;
  const instagramCanSend = instagramReady && !!instagramConversation.identity?.capabilities.send_text
    && Date.parse(instagramConversation.identity?.window_expires_at || '') > Date.now();
  const sendInstagramFile = async (file: File) => {
    const id = selectedContactId;
    try {
      const result = await instagramConversation.sendFile(file);
      if (result.status !== 'sent') throw new Error('Não foi possível confirmar o envio do arquivo pelo Instagram.');
      if (selectedContactLive.current?.id === id) sendMessage({ url: result.content_url }, result.message_type, String(result.message_id), 'instagram', {
        status: 'sent', providerMessageId: result.provider_message_id,
      });
    } catch (error) {
      if (selectedContactLive.current?.id === id) setSendError(parseApiError(error, 'Não foi possível confirmar o arquivo pelo Instagram.'));
    }
  };

  const contactPresence = useContactPresence({
    contactId: isSelectedContactIdentityResolved ? selectedContactId : null,
    phone: selectedIsInstagram ? null : selectedContact?.phone || null,
    enabled: isSelectedContactIdentityResolved && !selectedIsInstagram,
  });

  // Funções auxiliares
  const getInitials = (name: string): string => {
    if (!name || name.startsWith('+') || /^\d+$/.test(name.substring(0, 2))) {
      return 'CT';
    }
    const parts = name.split(' ').filter(part => part.length > 0);
    if (parts.length === 0) return 'CT';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Carrega informações da clínica
  useEffect(() => {
    async function loadClinicInfo() {
      try {
        const info = await getClinicInfo();
        setClinicInfo({
          name: info.name_clinic || 'Clínica',
          logo_url: info.logo_url
        });
      } catch (error) {
        console.error('Erro ao carregar informações da clínica:', error);
      }
    }
    loadClinicInfo();
  }, []);

  // Carrega contatos
  useEffect(() => {
    let active = true;
    const scope = contactDeliveryScope();
    async function loadContacts() {
      try {
        const contactList = await getContacts({ funnel_stages: chatFilters.funnelStages, lightweight: true, ...(attendanceEnabled ? { conversation_state: conversationState, assignment: assignmentFilter, archived_only: conversationState === 'archived' } : {}) });
        if (!active || scope !== contactDeliveryScope()) return;
        const previousContacts = contactsRef.current;
        const previousContactsById = new Map<number, Contact>();
        previousContacts.forEach((contact) => {
          const contactId = Number(contact.id);
          if (Number.isInteger(contactId) && contactId > 0) {
            previousContactsById.set(contactId, contact);
          }
        });
        if (!active) return;
        const cMap = new Map<string, Contact>();
        const modeReconciliations = new Map<number, string>();
        contactList.contacts.forEach((c) => {
          const normalizedPhone = conversationKey(c);
          if (!normalizedPhone) return;
          const contactId = Number(c.id);
          const knownMode = contactModeValueRef.current.get(contactId);
          const existing =
            previousContacts.get(normalizedPhone) ||
            previousContactsById.get(contactId) ||
            (
              knownMode !== undefined
                ? { id: contactId, human_mode: knownMode } as Contact
                : undefined
            );
          const modeDecision = resolvePolledContactMode(existing, c);
          if (
            modeDecision.requiresReconciliation &&
            Number.isInteger(contactId) &&
            contactId > 0
          ) {
            modeReconciliations.set(contactId, normalizedPhone);
          }
          const deliveryBaseline = existing?.id === c.id ? existing : undefined;
          const fresh = preserveCurrentContactMessage(deliveryBaseline, c);
          cMap.set(normalizedPhone, deliveryInboxRef.current.apply(scope, normalizedPhone, {
            ...fresh,
            ...mergeContactMessageDelivery(deliveryBaseline, fresh),
            phone: normalizePhone(c.phone),
            unreadCount: c.unreadCount ?? 0,
            human_mode: modeDecision.humanMode,
          }));
        });
        contactsRef.current = cMap;
        setContacts(cMap);
        modeReconciliations.forEach((phone, contactId) => {
          void reconcileContactMode(contactId, phone);
        });
      } catch (error) {
        console.error('Erro ao carregar contatos:', error);
      }
    }
    loadContacts();
    return () => { active = false; };
  }, [attendanceEnabled, conversationState, attendanceRevision, assignmentFilter, chatFilters]);

  useEffect(() => {
    if (!shouldSearchPhoneBackend) {
      setPhoneSearchResults([]);
      setIsPhoneSearchLoading(false);
      return;
    }

    let cancelled = false;
    const scope = contactDeliveryScope();
    setPhoneSearchResults([]);
    setIsPhoneSearchLoading(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await getContacts({ funnel_stages: chatFilters.funnelStages,
          ...(attendanceEnabled ? { conversation_state: conversationState, assignment: assignmentFilter, archived_only: conversationState === 'archived' } : {}),
          search: numericSearchDigits,
          unread_only: showUnreadOnly,
          limit: 50,
          lightweight: true,
        });
        if (cancelled || scope !== contactDeliveryScope()) return;

        setPhoneSearchResults(response.contacts.map((contact) => {
          const key = conversationKey(contact);
          const existing = contactsRef.current.get(key);
          const deliveryBaseline = existing?.id === contact.id ? existing : undefined;
          const fresh = preserveCurrentContactMessage(deliveryBaseline, contact);
          return deliveryInboxRef.current.apply(scope, key, withKnownContactMode({
            ...fresh,
            ...mergeContactMessageDelivery(deliveryBaseline, fresh),
            phone: String(contact.phone || '').replace(/\D/g, ''),
            unreadCount: contact.unreadCount ?? 0,
          }, contactModeValueRef.current));
        }));
      } catch {
        if (!cancelled) {
          setPhoneSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsPhoneSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [numericSearchDigits, shouldSearchPhoneBackend, showUnreadOnly, attendanceEnabled, conversationState, assignmentFilter, attendanceRevision, chatFilters]);

  const shouldApplyContactModeVersion = (
    contactId: number,
    auditId?: number | null,
    changedAt?: string | null,
    allowEqual = false,
  ): boolean =>
    registerContactModeVersion(
      contactModeVersionRef.current,
      contactId,
      auditId,
      changedAt,
      { allowEqual },
    );

  const applyAuthoritativeContactMode = ({
    contactId,
    phone,
    humanMode,
    auditId,
    changedAt,
    allowEqualVersion = false,
  }: {
    contactId: number;
    phone?: string;
    humanMode: boolean;
    auditId?: number | null;
    changedAt?: string | null;
    allowEqualVersion?: boolean;
  }): boolean => {
    if (!Number.isInteger(contactId) || contactId <= 0) return false;
    if (
      !shouldApplyContactModeVersion(
        contactId,
        auditId,
        changedAt,
        allowEqualVersion,
      )
    ) {
      return false;
    }

    contactModeValueRef.current.set(contactId, humanMode);
    setPhoneSearchResults((previous) =>
      applyContactModeToCollection(previous, contactId, humanMode)
    );
    const normalizedPhone = normalizePhone(phone || '');
    setContacts((previous) => {
      const next = new Map(previous);
      let changed = false;
      next.forEach((contact, key) => {
        const sameContact =
          Number(contact.id) === contactId ||
          (normalizedPhone !== '' &&
            key === normalizedPhone &&
            Number(contact.id) === contactId);
        if (sameContact && contact.human_mode !== humanMode) {
          next.set(key, { ...contact, human_mode: humanMode });
          changed = true;
        }
      });
      const result = changed ? next : previous;
      contactsRef.current = result;
      return result;
    });
    setSelectedContact((previous) =>
      previous && Number(previous.id) === contactId
        ? { ...previous, human_mode: humanMode }
        : previous
    );
    return true;
  };

  const invalidateContactModeReconciliation = (contactId: number) => {
    const nextGeneration =
      (contactModeReconcileGenerationRef.current.get(contactId) || 0) + 1;
    contactModeReconcileGenerationRef.current.set(contactId, nextGeneration);
    contactModeReconcileInFlightRef.current.delete(contactId);
  };

  const reconcileContactMode = (
    contactId: number,
    phone?: string,
  ): Promise<void> => {
    if (!Number.isInteger(contactId) || contactId <= 0) {
      return Promise.resolve();
    }

    const existingRequest = contactModeReconcileInFlightRef.current.get(contactId);
    if (existingRequest) return existingRequest;

    const generation =
      (contactModeReconcileGenerationRef.current.get(contactId) || 0) + 1;
    contactModeReconcileGenerationRef.current.set(contactId, generation);

    let request: Promise<void>;
    request = getContactMode(contactId)
      .then((response) => {
        if (
          contactModeReconcileGenerationRef.current.get(contactId) !== generation ||
          Number(response.contact_id) !== contactId
        ) {
          return;
        }

        applyAuthoritativeContactMode({
          contactId,
          phone: response.phone || phone,
          humanMode: response.human_mode === true,
          auditId: response.audit_id ?? response.audit?.id,
          changedAt: response.changed_at ?? response.audit?.created_at,
          allowEqualVersion: true,
        });
      })
      .catch((error) => {
        console.error('[ChatMobile] Falha ao reconciliar modo do contato:', {
          contactId,
          code: (error as any)?.code,
          status: (error as any)?.response?.status,
        });
      })
      .finally(() => {
        if (contactModeReconcileInFlightRef.current.get(contactId) === request) {
          contactModeReconcileInFlightRef.current.delete(contactId);
        }
      });

    contactModeReconcileInFlightRef.current.set(contactId, request);
    return request;
  };

  const handleContactModeConfirmed = (response: ContactModeResponse) => {
    return applyAuthoritativeContactMode({
      contactId: Number(response.contact_id),
      phone: response.phone,
      humanMode: response.human_mode === true,
      auditId: response.audit_id ?? response.audit?.id,
      changedAt: response.changed_at ?? response.audit?.created_at,
      allowEqualVersion: true,
    });
  };

  // WebSocket connection - COPIADO DO CHAT.TSX QUE FUNCIONA
  useEffect(() => {
    console.log('🔌 ChatMobile: Conectando WebSocket (lógica do Chat.tsx)...');
    unifiedWebSocketManager.connect();

    let active = true;
    const scope = contactDeliveryScope();
    const unsubscribeDelivery = unifiedWebSocketManager.onMessage('*', (event: any) => {
      if (!active || scope !== contactDeliveryScope()) return;
      const eventType = event.type || event.data?.type;
      if (eventType !== 'message_status' && eventType !== 'new_message') return;
      const value = event.data || event;
      const payload = {
        ...value,
        clinic_id: value.clinic_id ?? event.clinic_id,
        source_channel: value.source_channel ?? event.source_channel,
      };
      const key = conversationEventKey(event);
      const currentClinic = Number(localStorage.getItem('clinic_id'));
      if (!key || (payload.clinic_id != null && Number(payload.clinic_id) !== currentClinic)) return;
      const existing = contactsRef.current.get(key);
      const eventContactId = payload.contact_id ?? event.contact_id;
      if (existing && eventContactId != null && Number(eventContactId) !== Number(existing.id)) return;
      deliveryInboxRef.current.record(scope, key, payload);

      const updateContact = (contact: Contact): Contact => {
        if (conversationKey(contact) !== key ||
          (eventContactId != null && Number(eventContactId) !== Number(contact.id))) return contact;
        if (eventType === 'message_status') return deliveryInboxRef.current.apply(scope, key, contact);

        const providerId = payload.providerMessageId || payload.messageId;
        const messageId = typeof providerId === 'string' && providerId.length <= 255 ? providerId : null;
        const sameMessage = Boolean(messageId) && messageId === contact.lastMessageProviderId;
        const rawTime = payload.timestampNumber || payload.timestamp || payload.momment;
        const numericTime = rawTime !== null && rawTime !== undefined && rawTime !== '' ? Number(rawTime) : NaN;
        const timestamp = Number.isFinite(numericTime)
          ? (numericTime > 10000000000 ? numericTime : numericTime * 1000)
          : Date.parse(typeof rawTime === 'string' ? rawTime : '');
        const incomingTimestamp = Number.isFinite(timestamp) ? timestamp : Date.now();
        if (!sameMessage && incomingTimestamp < (contact.timestampNumber || 0)) return contact;
        const fromMe = payload.fromMe === true || payload.from_me === true;
        const incoming: Contact = {
          ...contact,
          lastMessage: typeof payload.content === 'string' ? payload.content
            : payload.content ? JSON.stringify(payload.content) : '',
          lastMessageType: payload.message_type || payload.type || null,
          timestampNumber: sameMessage ? Math.max(contact.timestampNumber || 0, incomingTimestamp) : incomingTimestamp,
          lastMessageFromMe: fromMe,
          lastMessageProviderId: messageId,
          lastMessageSourceChannel: payload.source_channel || (sameMessage ? contact.lastMessageSourceChannel : null),
          lastMessageStatus: payload.status || (fromMe ? 'sent' : undefined),
          lastMessageDeliveryError: payload.deliveryError,
          lastMessageDeliveryStatusAt: payload.deliveryStatusAt,
        };
        return deliveryInboxRef.current.apply(scope, key, {
          ...incoming,
          ...mergeContactMessageDelivery(contact, incoming),
        });
      };

      setContacts(previous => {
        const contact = previous.get(key);
        if (!contact) return previous;
        const updated = updateContact(contact);
        if (updated === contact) return previous;
        const next = new Map(previous);
        next.set(key, updated);
        contactsRef.current = next;
        return next;
      });
      setPhoneSearchResults(previous => previous.map(updateContact));
      setSelectedContact(previous => previous ? updateContact(previous) : previous);
    });
    // Polling para atualizar contatos (mesmo que Chat.tsx)
    const contactsPolling = setInterval(async () => {
      if (!active || scope !== contactDeliveryScope()) return;
      try {
        const updatedContacts = await getContacts({ funnel_stages: chatFilters.funnelStages, lightweight: true, ...(attendanceEnabled ? { conversation_state: conversationState, assignment: assignmentFilter, archived_only: conversationState === 'archived' } : {}) });
        if (!active || scope !== contactDeliveryScope()) return;
        const cMap = new Map<string, Contact>();
        const modeReconciliations = new Map<number, string>();

        updatedContacts.contacts.forEach((c) => {
          const normalizedPhone = conversationKey(c);
          if (!normalizedPhone) return;
          const existing = contactsRef.current.get(normalizedPhone);
          const knownMode = contactModeValueRef.current.get(Number(c.id));
          const modeBaseline = existing || (
            knownMode !== undefined
              ? { id: Number(c.id), human_mode: knownMode }
              : undefined
          );
          const modeDecision = resolvePolledContactMode(modeBaseline, c);
          if (
            modeDecision.requiresReconciliation &&
            Number.isInteger(Number(c.id)) &&
            Number(c.id) > 0
          ) {
            modeReconciliations.set(Number(c.id), normalizedPhone);
          }
          const deliveryBaseline = existing?.id === c.id ? existing : undefined;
          const fresh = preserveCurrentContactMessage(deliveryBaseline, c);
          cMap.set(normalizedPhone, deliveryInboxRef.current.apply(scope, normalizedPhone, {
            ...fresh,
            ...mergeContactMessageDelivery(deliveryBaseline, fresh),
            phone: normalizePhone(c.phone),
            unreadCount: c.unreadCount ?? 0,
            human_mode: modeDecision.humanMode,
          }));
        });

        contactsRef.current = cMap;
        setContacts(cMap);
        modeReconciliations.forEach((phone, contactId) => {
          void reconcileContactMode(contactId, phone);
        });
      } catch (error) {
        console.error('Erro ao atualizar contatos:', error);
      }
    }, 15000);

    return () => {
      active = false;
      unsubscribeDelivery();
      clearInterval(contactsPolling);
      contactModeReconcileGenerationRef.current.forEach((generation, contactId) => {
        contactModeReconcileGenerationRef.current.set(contactId, generation + 1);
      });
      contactModeReconcileInFlightRef.current.clear();
    };
  }, [attendanceEnabled, conversationState, assignmentFilter, attendanceRevision, chatFilters]);

  // Handler para contato selecionado - COPIADO DO CHAT.TSX
  useEffect(() => {
    if (!selectedContact) return;

    const normalizedPhone = conversationKey(selectedContact);
    console.log('📡 [ChatMobile] Inscrevendo no contato selecionado:', normalizedPhone);
    
    // Inscrever no tópico específico do contato selecionado
    unifiedWebSocketManager.subscribe(normalizedPhone);

    // Registrar handler para mensagens específicas deste contato
    const unsubscribeContact = unifiedWebSocketManager.onMessage(normalizedPhone, (data) => {
      if (data.type === 'contact_presence' || data.data?.type === 'contact_presence') return;
      if (selectedIsInstagram && data.type === 'new_message') {
        void instagramConversation.reload();
        const value = data.data || data;
        if (!value.fromMe) void markInstagramRead(selectedContact.id).catch(() => {});
      }

      console.log('📱 [ChatMobile] Mensagem WebSocket para contato selecionado:', {
        phone: data.phone,
        type: data.type,
        fromMe: data.fromMe
      });
      
      // O useOptimizedMessages automaticamente processa as mensagens
      // Aqui só atualizamos o estado do contato se necessário
      if (data.type === 'contact_mode_changed') {
        const currentClinicId = Number(localStorage.getItem('clinic_id'));
        const knownContactId = Number(selectedContact.id);

        if (
          !Number.isInteger(knownContactId) ||
          knownContactId <= 0
        ) {
          return;
        }

        const disposition = classifyContactModeEvent(
          data,
          currentClinicId,
          knownContactId,
        );
        if (disposition === 'ignore') return;

        const eventHumanMode = data.human_mode ?? data.data?.human_mode;
        if (disposition === 'reconcile' || typeof eventHumanMode !== 'boolean') {
          void reconcileContactMode(knownContactId, normalizedPhone);
          return;
        }

        applyAuthoritativeContactMode({
          contactId: knownContactId,
          phone: normalizedPhone,
          humanMode: eventHumanMode,
          auditId: data.audit_id ?? data.data?.audit_id,
          changedAt: data.changed_at ?? data.data?.changed_at,
        });
      }
    });

    // Limpar quando mudar de contato ou desmontar
    return () => {
      unsubscribeContact();
      unifiedWebSocketManager.unsubscribe(normalizedPhone);
      const subscribedContactId = Number(selectedContact.id);
      if (Number.isInteger(subscribedContactId) && subscribedContactId > 0) {
        invalidateContactModeReconciliation(subscribedContactId);
      }
    };
  }, [selectedContact?.id, selectedContact?.phone, selectedContact?.active_channel]);

  // Carregar status do flow control
  const loadContactFlowStatus = async (phone: string) => {
    try {
      setIsLoadingFlowStatus(true);
      const clinicId = localStorage.getItem('clinic_id');
      
      if (!clinicId) return;
      
      const response = await api.get(
        `/api/flow-control/contact/${clinicId}/${encodeURIComponent(phone)}`,
      );
      setContactFlowStatus(response.data.flows || {});
    } catch (error) {
      console.error('Erro ao carregar status:', error);
    } finally {
      setIsLoadingFlowStatus(false);
    }
  };

  // Toggle flow control individual
  const toggleContactFlow = async (flowType: string, isPaused: boolean, reason: string) => {
    try {
      const clinicId = localStorage.getItem('clinic_id');
      
      if (!clinicId || !selectedContact) return;
      
      await api.post(`/api/flow-control/contact/${clinicId}/toggle`, {
          contact_identifier: selectedContact.phone,
          identifier_type: 'phone',
          flow_type: flowType,
          is_paused: isPaused,
          reason: reason
      });
      
      await loadContactFlowStatus(selectedContact.phone);
    } catch (error) {
      console.error('Erro ao alternar flow:', error);
    }
  };

  // Selecionar contato
  const selectContact = async (contact: Contact) => {
    const generation = ++selectionGeneration.current;
    const normalizedPhone = conversationKey(contact);
    const contactWithKnownMode = withKnownContactMode(
      contact,
      contactModeValueRef.current,
    );
    
    try {
      if ((contact.active_channel || contact.source_channel) === 'instagram') await markInstagramRead(contact.id);
      else await markContactAsRead(normalizedPhone);
    } catch (err) {
      console.error('Erro ao marcar contato como lido:', err);
    }
    if (generation !== selectionGeneration.current) return;

    setContacts((prev) => {
      const newMap = new Map(prev);
      if (prev.has(normalizedPhone)) {
        const updatedContact = {
          ...prev.get(normalizedPhone)!,
          unreadCount: 0
        };
        newMap.set(normalizedPhone, updatedContact);
      }
      return newMap;
    });

    setSelectedContact({ ...contactWithKnownMode, unreadCount: 0 });
    setNewMessage('');
    setShowFlowControl(false);
    setSendError(null);
    setCurrentView('chat');
    
    // Carregar status do flow control
    if ((contact.active_channel || contact.source_channel) !== 'instagram') loadContactFlowStatus(normalizedPhone);
  };

  useEffect(() => {
    const incoming = readChatNavigationPayload(location.state as ChatNavigationState);
    if (incoming?.isInstagram && incoming.selectedContactId > 0) {
      let active = true;
      setCurrentView('chat'); setSelectedContact(null); setNewMessage('');
      const resolveInstagram = async () => {
        try {
          let offset = 0;
          while (active) {
            const response = await getContacts({ offset, limit: 100, lightweight: true, show_archived: true });
            const contact = response.contacts.find(row => Number(row.id) === incoming.selectedContactId && row.active_channel === 'instagram');
            if (contact) { if (active) await selectContact({ ...contact, phone: '' }); return; }
            if (!response.has_more || !response.contacts.length) break;
            offset += 100;
          }
          if (active) { setCurrentView('list'); setSendError({ message: 'Conversa do Instagram indisponível para este acesso.' }); }
        } catch { if (active) { setCurrentView('list'); setSendError({ message: 'Não foi possível localizar a conversa do Instagram.' }); } }
        finally { if (active) navigate(location.pathname, { replace: true, state: null }); }
      };
      void resolveInstagram();
      return () => { active = false; };
    }
    if (incoming && !incoming.isInstagram && incoming.rawPhone) {
      pendingRouteNavigationRef.current = incoming;
      setPendingChatNavigation(incoming);
      setSelectedContact(null);
      setNewMessage('');
      setShowTaskPanel(false);
      setShowFlowControl(false);
      setContactFlowStatus({});
      setCurrentView('chat');
      setSendError({
        code: 'contact_identity_pending',
        message: 'Identificando o contato selecionado antes de liberar o envio.',
        existingContactId: incoming.selectedContactId > 0 ? incoming.selectedContactId : undefined,
        existingPhone: incoming.normalizedPhone,
      });
      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    const payload = pendingRouteNavigationRef.current;
    if (!payload || payload.isInstagram || !payload.rawPhone) return;

    let cancelled = false;
    const contactId = payload.selectedContactId;
    const normalizedPhone = payload.normalizedPhone;
    const rawPhone = payload.rawPhone;

    const resolveNavigationContact = async () => {
      try {
        const response = await getContacts({
          search: rawPhone,
          limit: 50,
          lightweight: true,
          show_archived: true,
        });
        const resolvedContact = pickNavigationContact(response.contacts, payload, normalizePhone);

        if (cancelled) return;

        if (!resolvedContact) {
          setSelectedContact(null);
          setCurrentView('list');
          setSendError({
            message:
              'Não foi possível identificar um único contato para esta conversa. Confira os cadastros pelo telefone.',
            existingContactId: contactId > 0 ? contactId : undefined,
            existingPhone: normalizedPhone,
          });
          return;
        }

        const resolvedPhone = normalizePhone(resolvedContact.phone);
        const normalizedContact = withKnownContactMode({
          ...resolvedContact,
          phone: resolvedPhone,
          unreadCount: resolvedContact.unreadCount ?? 0,
        }, contactModeValueRef.current);
        setContacts((previous) => {
          const next = new Map(previous);
          next.set(resolvedPhone, normalizedContact);
          return next;
        });
        setSelectedContact(normalizedContact);
        setCurrentView('chat');
        setSendError(
          normalizedContact.archived
            ? {
                code: 'archived_contact',
                message:
                  'Este cadastro está arquivado. Selecione o cadastro ativo antes de enviar.',
                existingContactId: normalizedContact.id,
                existingPhone: normalizedContact.phone,
              }
            : null,
        );
        await markContactAsRead(resolvedPhone);
        loadContactFlowStatus(resolvedPhone);
      } catch (error) {
        if (!cancelled) {
          setSelectedContact(null);
          setCurrentView('list');
          setSendError(
            parseApiError(
              error,
              'Não foi possível localizar o contato selecionado.',
            ),
          );
        }
      } finally {
        if (!cancelled) {
          pendingRouteNavigationRef.current = null;
          setPendingChatNavigation(null);
        }
      }
    };

    resolveNavigationContact();

    return () => {
      cancelled = true;
    };
  }, [location.key, location.pathname, location.state, navigate]);

  // Enviar mensagem
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedContact) return;
    if (selectedIsInstagram) {
      const id = selectedContact.id, content = newMessage;
      try {
        const result = await instagramConversation.send(content);
        if (result.status !== 'sent') throw new Error('O envio pelo Instagram não foi confirmado. O texto foi preservado.');
        if (contactsRef.current.get(`contact:${id}`)) setContacts(previous => {
          const next = new Map(previous), old = next.get(`contact:${id}`);
          if (old) next.set(`contact:${id}`, { ...old, lastMessage: content, lastMessageType: 'text', timestampNumber: Date.now() });
          return next;
        });
        if (selectedContactLive.current?.id !== id) return;
        sendMessage(content, 'text', String(result.message_id), 'instagram', { status: 'sent', providerMessageId: result.provider_message_id });
        setNewMessage(current => current === content ? '' : current); setSendError(null);
      } catch (error) {
        if (selectedContactLive.current?.id === id) setSendError(parseApiError(error, 'Não foi possível confirmar o Direct.'));
      }
      return;
    }
    const contactId = requireSelectedContactId();
    if (!contactId) return;

    const localMessageId = `local_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    try {
      setSendError(null);
      sendMessage(newMessage, 'text', localMessageId);
      const sendResult = await sendWhatsAppText({
        phone: selectedContact.phone, 
        message: newMessage,
        localMessageId: localMessageId,
        contact_id: contactId,
      });
      updateMessageTracking(localMessageId, extractProviderMessageId(sendResult), 'sent', sendResult?.sent_content);
      setNewMessage('');
    } catch (error) {
      updateMessageTracking(localMessageId, null, 'failed');
      setSendError(parseApiError(error, 'Não foi possível enviar a mensagem.'));
    }
  };

  // Enviar áudio
  const handleAudioRecorded = async (audioBlob: Blob, recordedDuration: number) => {
    if (!selectedContact) return;
    if (selectedIsInstagram) {
      let file = instagramAudioFiles.current.get(audioBlob);
      if (!file) { file = new File([audioBlob], 'audio.webm', { type: audioBlob.type }); instagramAudioFiles.current.set(audioBlob, file); }
      await sendInstagramFile(file); return;
    }
    const contactId = requireSelectedContactId();
    if (!contactId) return;

    try {
      setSendError(null);
      const tempUrl = URL.createObjectURL(audioBlob);
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        if (!e.target?.result) return;
        const localMessageId = `local_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        try {
          const base64Content = e.target.result.toString();

          sendMessage({
            url: tempUrl,
            mimeType: audioBlob.type,
            duration: recordedDuration
          }, 'audio', localMessageId);

          await sendWhatsAppAudio({
            phone: selectedContact.phone,
            audio: base64Content,
            localMessageId: localMessageId,
            contact_id: contactId,
          });
        } catch (error) {
          updateMessageTracking(localMessageId, null, 'failed');
          setSendError(parseApiError(error, 'Não foi possível enviar o áudio.'));
        }
      };

      reader.readAsDataURL(audioBlob);
    } catch (err) {
      setSendError(parseApiError(err, 'Não foi possível enviar o áudio.'));
    }
  };

  const handleReviewDuplicateContacts = () => {
    const phone = sendError?.existingPhone || selectedContact?.phone;
    if (!phone) return;

    navigate('/contacts', {
      state: {
        searchTerm: phone,
        includeArchived: true,
        reviewDuplicates: true,
      },
    });
  };

  // Enviar NPS
  const handleSendNPS = async () => {
    if (!selectedContact) return;
    const contactId = requireSelectedContactId();
    if (!contactId) return;

    try {
      setSendError(null);
      const question = 'Em uma escala de 1 a 5, como você avalia nosso atendimento?';
      
      // Enviar direto para o backend (que irá salvar na tabela messages)
      const response = await sendNPS({
        phone: selectedContact.phone,
        contact_id: contactId,
        question,
        campaign_name: 'manual_chat'
      });
      
      // Adicionar mensagem NPS localmente para aparecer imediatamente
      if (response && typeof response === 'object' && 'nps_id' in response) {
        const npsMessageContent = {
          nps_data: {
            question: question,
            nps_id: response.nps_id,
            message_id: response.message_id,
            status: 'sent',
            campaign_name: 'manual_chat'
          }
        };
        sendMessage(npsMessageContent, 'nps', `local_nps_${Date.now()}`);
      }
      
    } catch (error) {
      setSendError(parseApiError(error, 'Não foi possível enviar a pesquisa NPS.'));
    }
  };

  // Filtrar contatos
  const filteredContacts = (
    shouldSearchPhoneBackend ? phoneSearchResults : Array.from(contacts.values())
  )
    .filter(c =>
      shouldSearchPhoneBackend ||
      Boolean(c.timestampNumber && c.timestampNumber > 0)
    )
    .filter(c => !showUnreadOnly || c.unreadCount > 0)
    .filter(c => {
      if (!searchTerm || shouldSearchPhoneBackend) return true;
      const search = searchTerm.toLowerCase();
      return c.name?.toLowerCase().includes(search) || 
             c.phone?.toLowerCase().includes(search);
    })
    .sort((a, b) => (b.timestampNumber || 0) - (a.timestampNumber || 0));

  // Renderizar lista de contatos
  const renderContactList = () => (
    <div className="flex flex-col h-full bg-gray-50 overflow-hidden pb-16">
      <div className="bg-white border-b px-4 py-3">
        <h1 className="mb-3 text-lg font-semibold">Conversas</h1>
        <div className="flex items-center gap-2">
          <input aria-label="Buscar contato" type="text" placeholder="Buscar contato..." className="h-10 min-w-0 flex-1 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm" value={searchTerm} onChange={event => setSearchTerm(event.target.value)} />
          <ChatFilterControl filters={chatFilters} onChange={setChatFilters} assignment={attendanceEnabled ? assignmentFilter : undefined} onAssignmentChange={attendanceEnabled ? setAssignmentFilter : undefined} />
        </div>
        <div className="mt-2"><ChatFilterChips filters={chatFilters} onChange={setChatFilters} assignment={attendanceEnabled ? assignmentFilter : undefined} onAssignmentChange={attendanceEnabled ? setAssignmentFilter : undefined} /></div>
      </div>

      {/* Seletor principal de conversas */}
      {attendanceEnabled !== false ? <div className="bg-white border-b px-4 py-2"><ConversationTabs loading={attendanceEnabled === null} assignment={assignmentFilter} value={conversationState} onChange={setConversationState} /></div> : <div className="bg-white border-b px-4 py-2 flex gap-2">
        <button
          onClick={() => setShowUnreadOnly(false)}
          className={`flex-1 py-1.5 text-sm rounded-lg ${
            !showUnreadOnly ? 'bg-blue-500 text-white' : 'bg-gray-100'
          }`}
        >
          Todas
        </button>
        <button
          onClick={() => setShowUnreadOnly(true)}
          className={`flex-1 py-1.5 text-sm rounded-lg ${
            showUnreadOnly ? 'bg-blue-500 text-white' : 'bg-gray-100'
          }`}
        >
          Não lidas
        </button>
      </div>}


      {sendError && sendError.code !== 'archived_contact' && (
        <WhatsAppIdentityErrorAlert
          error={sendError}
          variant="bottom-bar"
          onOpenExisting={handleReviewDuplicateContacts}
          existingActionLabel="Revisar cadastros duplicados"
          showExistingAction={
            Boolean(sendError.existingContactId) ||
            sendError.code === 'whatsapp_identity_conflict'
          }
        />
      )}

      {/* Lista de contatos */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <span
          aria-atomic="true"
          aria-live="polite"
          className="sr-only"
          data-testid="mobile-contact-list-typing-announcement"
        >
          {contactPresence.activity === 'typing' && selectedContact
            ? `${selectedContact.name || 'Contato'} está digitando`
            : ''}
        </span>
        {isPhoneSearchLoading && (
          <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Buscando telefone…
          </div>
        )}
        {filteredContacts.map((contact) => {
          const isSelectedContactTyping =
            contactPresence.activity === 'typing' &&
            selectedContact !== null &&
            normalizePhone(selectedContact.phone) === normalizePhone(contact.phone);

          return (
            <div
              key={contact.id || contact.phone}
              onClick={() => selectContact(contact)}
              role="presentation"
              className={`relative w-full border-b px-4 py-3 flex items-center gap-3 overflow-hidden ${
                isDark ? 'bg-sminy-dark-surface border-sminy-dark-border text-sminy-dark-text-primary hover:bg-sminy-dark-elevated'
                  : 'bg-white border-sminy-border text-sminy-text-primary hover:bg-sminy-canvas'}`}
            >
            <button
              type="button"
              aria-label={`Selecionar conversa com ${contact.name || contact.phone}`}
              aria-pressed={selectedContact?.id === contact.id}
              onClick={event => { event.stopPropagation(); void selectContact(contact); }}
              className="absolute inset-0 w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary/40"
            />
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {contact.photo ? (
                  <img src={contact.photo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-gray-600 font-medium">
                    {getInitials(contact.name || contact.phone)}
                  </span>
                )}
              </div>
              <ChatChannelBadge channel={resolveContactDisplayChannel(contact.active_channel, contact.lastMessageSourceChannel)} />
            </div>

            {/* Conteúdo */}
            <div className="relative flex-1 min-w-0 text-left">
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-1.5">
                  <p className="min-w-0 flex-1 truncate text-sm font-medium">{contact.name}</p>
                  <span
                    aria-label={`Atendimento: ${contact.human_mode ? 'Humano' : 'IA'}`}
                    className={`flex-shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold leading-none ${
                      contact.human_mode
                        ? 'border-rose-200 bg-rose-50 text-rose-700'
                        : 'border-sminy-primary/20 bg-sminy-primary/10 text-sminy-primary'
                    }`}
                  >
                    {contact.human_mode ? 'Humano' : 'IA'}
                  </span>
                </div>
                <span className={`text-xs flex-shrink-0 ${isDark ? 'text-sminy-dark-muted' : 'text-sminy-muted'}`}>
                  {formatChatTimestamp(contact.timestampNumber)}
                </span>
              </div>
              {(isNumericSearch || contact.possible_duplicate) && (
                <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5">
                  <span className="truncate text-xs text-gray-500">
                    {formatBrazilianPhone(contact.phone)}
                  </span>
                  {contact.possible_duplicate && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                      Possível duplicidade
                    </span>
                  )}
                </div>
              )}
              <div className={`mt-0.5 flex h-4 min-w-0 items-center text-xs ${isDark ? 'text-sminy-dark-muted' : 'text-sminy-muted'}`}>
                {isSelectedContactTyping ? (
                  <span
                    aria-hidden="true"
                    className="flex h-4 items-center truncate"
                    data-testid="mobile-contact-list-typing-indicator"
                  >
                    digitando...
                  </span>
                ) : (
                  <>
                    {contact.lastMessageFromMe === true && (
                      contact.lastMessageStatus === 'failed'
                        ? <DeliveryFailureHint compact error={contact.lastMessageDeliveryError} />
                        : <span className="mr-1 shrink-0" aria-label="Status da última mensagem">
                            {contact.lastMessageStatus === 'sending' ? <Clock className="h-3 w-3" />
                              : contact.lastMessageStatus === 'sent' ? <Check className="h-3 w-3" />
                              : contact.lastMessageStatus === 'delivered' || contact.lastMessageStatus === 'read'
                                ? <CheckCheck className={`h-3 w-3 ${contact.lastMessageStatus === 'read' ? 'text-sminy-primary' : ''}`} /> : null}
                          </span>
                    )}
                    <span className="truncate">{formatLastMessage(contact.lastMessage, contact.lastMessageType).text || 'Clique para conversar'}</span>
                  </>
                )}
              </div>
              {contact.funnel_stage && <div className="mt-1 flex"><StatusTag status={contact.funnel_stage} /></div>}
            </div>

            {/* Badges */}
            <div className="relative flex items-center gap-1 flex-shrink-0">
              {/* Badge de tarefas pendentes */}
              {contact.pending_tasks_count && contact.pending_tasks_count > 0 && (
                <div 
                  className={`w-5 h-5 rounded-full text-white flex items-center justify-center text-[10px] font-bold ${
                    contact.next_task?.priority === 'urgent' ? 'bg-red-500' :
                    contact.next_task?.priority === 'high' ? 'bg-orange-500' :
                    'bg-blue-500'
                  }`}
                  title={`${contact.pending_tasks_count} tarefa(s) - ${contact.next_task?.title || ''}`}
                >
                  <CheckCircle2 className="w-3 h-3" />
                </div>
              )}
              
              {/* Badge não lidas */}
              {contact.unreadCount > 0 && (
                <div className="min-w-[20px] w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-[10px] text-white font-bold">
                    {contact.unreadCount > 9 ? '9+' : contact.unreadCount}
                  </span>
                </div>
              )}
            </div>
            </div>
          );
        })}
        {!isPhoneSearchLoading && filteredContacts.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            Nenhum contato encontrado
          </div>
        )}
      </div>
    </div>
  );

  // Renderizar chat
  const renderChat = () => (
    <div className={`flex h-full flex-col overflow-hidden ${isDark ? 'bg-slate-950 text-slate-50' : 'bg-slate-50 text-slate-900'}`}>
      {/* Header do chat */}
      <div className={`border-b ${isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          onClick={() => setCurrentView('list')}
          className="-ml-1 flex min-h-11 min-w-11 items-center justify-center rounded-xl hover:bg-gray-100"
          aria-label="Voltar para a lista de contatos"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        <div className="flex-1 min-w-0 flex items-center gap-3">
          <div className="relative shrink-0"><div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
            {selectedContact?.photo ? (
              <img src={selectedContact.photo} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-gray-600 font-medium">
                {getInitials(selectedContact?.name || '')}
              </span>
            )}
          </div><ChatChannelBadge channel={resolveContactDisplayChannel(selectedContact?.active_channel, selectedContact?.lastMessageSourceChannel || selectedContact?.source_channel)} /></div>
          <div className="flex-1 min-w-0">
            <div className="flex min-w-0 items-center gap-1.5">
              <p className="min-w-0 flex-1 truncate text-sm font-medium">{selectedContact?.name}</p>
              <span
                aria-label={`Modo de atendimento: ${selectedContact?.human_mode ? 'Humano ativo' : 'IA ativa'}`}
                className={`flex-shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold ${
                  selectedContact?.human_mode
                    ? 'border-rose-200 bg-rose-50 text-rose-700'
                    : 'border-blue-200 bg-blue-50 text-blue-700'
                }`}
              >
                {selectedContact?.human_mode ? 'Humano ativo' : 'IA ativa'}
              </span>
            </div>
            <ContactPresenceStatusLine
              phone={selectedContact?.phone || ''}
              availability={contactPresence.availability}
              lastSeen={contactPresence.lastSeen}
            />
          </div>
        </div>

        {/* Botão de tarefas */}
        {!selectedIsInstagram && <button
          onClick={() => setShowTaskPanel(true)}
          className="relative flex min-h-11 min-w-11 items-center justify-center rounded-xl hover:bg-gray-100"
          aria-label="Abrir tarefas"
        >
          <CheckCircle2 className="w-5 h-5 text-blue-600" />
          {selectedContact?.pending_tasks_count && selectedContact.pending_tasks_count > 0 && (
            <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full text-[8px] text-white flex items-center justify-center font-bold ${
              selectedContact.next_task?.priority === 'urgent' ? 'bg-red-500' :
              selectedContact.next_task?.priority === 'high' ? 'bg-orange-500' :
              'bg-blue-500'
            }`}>
              {selectedContact.pending_tasks_count}
            </div>
          )}
        </button>}
        
        {/* Botão de Flow Control */}
        {!selectedIsInstagram && <button
          onClick={() => setShowFlowControl(!showFlowControl)}
          className="relative flex min-h-11 min-w-11 items-center justify-center rounded-xl hover:bg-gray-100"
          title="Controle de fluxos automáticos"
          aria-label="Controle de fluxos automáticos"
        >
          <Target className="w-5 h-5 text-purple-600" />
          {/* Indicador se algum fluxo está pausado */}
          {Object.values(contactFlowStatus).some((flow: any) => flow.is_paused) && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
          )}
        </button>}
        <div ref={attendanceCloseTarget} className="shrink-0" />
        
      </div>


      </div>

      {attendanceEnabled !== false && selectedContact && <ContactAttendance key={selectedContact.id} enabled={attendanceEnabled} renderModeControl={(humanMode, onAssume, onModeChanged, busy) => <ContactModeControl contactId={selectedContact.id} contactLabel={selectedContact.name || selectedContact.phone} humanMode={humanMode} identityResolved={isSelectedContactIdentityResolved && !busy} onAssume={onAssume} onModeConfirmed={response => { const accepted = handleContactModeConfirmed(response); if (accepted !== false) onModeChanged(response.human_mode === true); return accepted; }} variant="compact" />} claimTarget={attendanceClaimTarget} closeTarget={attendanceCloseTarget} contactId={selectedContact.id} onChanged={(action, data) => { setAttendanceRevision(value => value + 1); if (action === 'release' || action === 'transfer' || action === 'close') { setSelectedContact(null); } else { setSelectedContact(current => current ? { ...current, human_mode: data.human_mode } : current); } }} />}
      {attendanceEnabled && selectedContact && <details className="border-b px-4 py-2"><summary className="text-xs cursor-pointer">Histórico de atendimento</summary><ContactAttendance contactId={selectedContact.id} historyOnly /></details>}
      {/* Painel de controle de fluxos */}
      {showFlowControl && (
        <div className="border-b border-gray-200 bg-gray-50 p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Controle de Mensagens Automáticas</h3>
          
          {isLoadingFlowStatus ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Follow-up */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">Follow-up</p>
                  <p className="text-xs text-gray-500">Mensagens de acompanhamento</p>
                </div>
                <button
                  onClick={() => {
                    const flow = contactFlowStatus.follow_up || {};
                    toggleContactFlow('follow_up', !flow.is_paused,
                      flow.is_paused ? 'Retomado via chat mobile' : 'Pausado via chat mobile');
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    contactFlowStatus.follow_up?.is_paused ? 'bg-gray-200' : 'bg-green-500'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    contactFlowStatus.follow_up?.is_paused ? 'translate-x-1' : 'translate-x-6'
                  }`} />
                </button>
              </div>

              {/* Confirmação de Consulta */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">Confirmação de Consulta</p>
                  <p className="text-xs text-gray-500">Lembrete de agendamento</p>
                </div>
                <button
                  onClick={() => {
                    const flow = contactFlowStatus.confirmation || {};
                    toggleContactFlow('confirmation', !flow.is_paused,
                      flow.is_paused ? 'Retomado via chat mobile' : 'Pausado via chat mobile');
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    contactFlowStatus.confirmation?.is_paused ? 'bg-gray-200' : 'bg-green-500'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    contactFlowStatus.confirmation?.is_paused ? 'translate-x-1' : 'translate-x-6'
                  }`} />
                </button>
              </div>

              {/* No-show */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">No-show</p>
                  <p className="text-xs text-gray-500">Reagendamento após falta</p>
                </div>
                <button
                  onClick={() => {
                    const flow = contactFlowStatus.noshow || {};
                    toggleContactFlow('noshow', !flow.is_paused,
                      flow.is_paused ? 'Retomado via chat mobile' : 'Pausado via chat mobile');
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    contactFlowStatus.noshow?.is_paused ? 'bg-gray-200' : 'bg-green-500'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    contactFlowStatus.noshow?.is_paused ? 'translate-x-1' : 'translate-x-6'
                  }`} />
                </button>
              </div>

              {/* Pós-consulta */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">Pós-consulta</p>
                  <p className="text-xs text-gray-500">Follow-up após atendimento</p>
                </div>
                <button
                  onClick={() => {
                    const flow = contactFlowStatus.pos_consulta || {};
                    toggleContactFlow('pos_consulta', !flow.is_paused,
                      flow.is_paused ? 'Retomado via chat mobile' : 'Pausado via chat mobile');
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    contactFlowStatus.pos_consulta?.is_paused ? 'bg-gray-200' : 'bg-green-500'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    contactFlowStatus.pos_consulta?.is_paused ? 'translate-x-1' : 'translate-x-6'
                  }`} />
                </button>
              </div>

              {/* Pós-venda */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">Pós-venda</p>
                  <p className="text-xs text-gray-500">Acompanhamento após venda</p>
                </div>
                <button
                  onClick={() => {
                    const flow = contactFlowStatus.pos_venda || {};
                    toggleContactFlow('pos_venda', !flow.is_paused,
                      flow.is_paused ? 'Retomado via chat mobile' : 'Pausado via chat mobile');
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    contactFlowStatus.pos_venda?.is_paused ? 'bg-gray-200' : 'bg-green-500'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    contactFlowStatus.pos_venda?.is_paused ? 'translate-x-1' : 'translate-x-6'
                  }`} />
                </button>
              </div>

              {/* Botão para pausar/retomar todos */}
              <div className="pt-3 border-t border-gray-200">
                <button
                  onClick={() => {
                    const anyActive = Object.values(contactFlowStatus).some((flow: any) => !flow.is_paused);
                    toggleContactFlow('all', anyActive,
                      anyActive ? 'Todos os fluxos pausados via chat mobile' : 'Todos os fluxos retomados via chat mobile');
                  }}
                  className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
                >
                  {Object.values(contactFlowStatus).some((flow: any) => !flow.is_paused) 
                    ? 'Pausar Todos os Fluxos' 
                    : 'Retomar Todos os Fluxos'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Container com scroll que comporta mensagens */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <VirtualizedMessageList
          messages={messages}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={loadMoreMessages}
          instagramScope={selectedIsInstagram && instagramConversation.identity ? { contactId: selectedContactId, identityId: instagramConversation.identity.identity_id } : undefined}
          canReplyComment={instagramComments.canReply && !instagramConversation.sending}
          onReplyComment={instagramComments.select}
          onCorrectAgentMessage={message => { setCorrectionMessage(message); setCorrectionContactId(selectedContactId); setCorrectionClinicId(activeClinicId()); }}
          canManageLearning={hasPermission('prompt') && isSelectedContactIdentityResolved && !selectedIsInstagram}
          contactId={selectedContactId}
          endAdornment={(
            <ContactPresenceBubble activity={contactPresence.activity} />
          )}
          endAdornmentKey={contactPresence.activity}
        />
      </div>

      {/* Rodapé no fluxo: reserva sua altura real e respeita a safe-area */}
      <div className={`z-10 flex-shrink-0 pb-[env(safe-area-inset-bottom)] ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        {sendError && sendError.code !== 'archived_contact' && (
          <div className="max-h-[35vh] overflow-y-auto">
            <WhatsAppIdentityErrorAlert
              error={sendError}
              variant="top-bar"
              onOpenExisting={handleReviewDuplicateContacts}
              existingActionLabel="Revisar cadastros duplicados"
              showExistingAction={
                Boolean(sendError.existingContactId) ||
                sendError.code === 'whatsapp_identity_conflict'
              }
            />
          </div>
        )}
        {selectedIsInstagram && instagramComments.target ? <InstagramCommentComposer state={instagramComments} /> : <>
        {selectedIsInstagram && <p className="px-3 pt-2 text-xs text-slate-500">Direct · mensagem privada</p>}
        <OptimizedMessageInput
          extraControl={<><div ref={attendanceClaimTarget} />{attendanceEnabled === false && <ContactModeControl

          contactId={selectedContact?.id}
          contactLabel={selectedContact?.name || selectedContact?.phone}
          humanMode={selectedContact?.human_mode === true}
          identityResolved={isSelectedContactIdentityResolved}
          onModeConfirmed={handleContactModeConfirmed}
          variant="compact"
        />}</>}
          onSendText={handleSendMessage}
          onSendAudio={handleAudioRecorded}
          onSendNPS={selectedIsInstagram ? undefined : handleSendNPS}
          onRecordingStateChange={selectedIsInstagram ? undefined : contactPresence.onRecordingStateChanged}
          preserveDraftOnSubmit={selectedIsInstagram}
          textDisabled={selectedIsInstagram && !instagramCanSend}
          mediaDisabled={selectedIsInstagram && !(instagramCanSend && instagramConversation.identity?.capabilities.upload_private_media)}
          recordingContextKey={`${selectedContact.id}:${selectedContact.phone}`}
          onSendImage={async (file) => {
            if (!selectedContact) return;
            if (selectedIsInstagram) { await sendInstagramFile(file); return; }
            const contactId = requireSelectedContactId();
            if (!contactId) return;
            
            const reader = new FileReader();
            reader.onload = async (e) => {
              if (!e.target?.result) return;
              const localMessageId = `local_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
              try {
                setSendError(null);
                const base64Content = e.target.result.toString();

                sendMessage({
                  url: URL.createObjectURL(file),
                  mimeType: file.type
                }, 'image', localMessageId);

                const sendResult = await sendWhatsAppImage({
                  phone: selectedContact.phone,
                  image: base64Content,
                  localMessageId: localMessageId,
                  contact_id: contactId,
                });
                updateMessageTracking(localMessageId, extractProviderMessageId(sendResult), 'sent', sendResult?.sent_content);
              } catch (error) {
                updateMessageTracking(localMessageId, null, 'failed');
                setSendError(parseApiError(error, 'Não foi possível enviar a imagem.'));
              }
            };
            reader.readAsDataURL(file);
          }}
          onSendVideo={async (file) => {
            if (!selectedContact) return;
            if (selectedIsInstagram) { await sendInstagramFile(file); return; }
            const contactId = requireSelectedContactId();
            if (!contactId) return;
            
            const reader = new FileReader();
            reader.onload = async (e) => {
              if (!e.target?.result) return;
              const localMessageId = `local_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
              try {
                setSendError(null);
                const base64Content = e.target.result.toString();

                sendMessage({
                  url: URL.createObjectURL(file),
                  mimeType: file.type
                }, 'video', localMessageId);

                const sendResult = await sendWhatsAppVideo({
                  phone: selectedContact.phone,
                  video: base64Content,
                  localMessageId: localMessageId,
                  contact_id: contactId,
                });
                updateMessageTracking(localMessageId, extractProviderMessageId(sendResult), 'sent', sendResult?.sent_content);
              } catch (error) {
                updateMessageTracking(localMessageId, null, 'failed');
                setSendError(parseApiError(error, 'Não foi possível enviar o vídeo.'));
              }
            };
            reader.readAsDataURL(file);
          }}
          disabled={!isSelectedContactIdentityResolved || (selectedIsInstagram && !instagramReady)}
          placeholder={
            selectedIsInstagram && !instagramCanSend ? 'Aguarde uma nova mensagem para responder por Direct.' : isSelectedContactIdentityResolved
              ? 'Digite...'
              : 'Aguardando identificação segura do contato...'
          }
          value={newMessage}
          onChange={(value) => {
            setNewMessage(value);
            if (!selectedIsInstagram) contactPresence.onTextChanged(value);
          }}
          onTextBlur={selectedIsInstagram ? undefined : contactPresence.onTextBlur}
        />
        </>}
      </div>
    </div>
  );


  return (
    <div className="fixed inset-0 overflow-hidden">
      {currentView === 'list' || !selectedContact ? renderContactList() : renderChat()}
      
      
      {/* TaskPanelMobile */}
      {showTaskPanel && selectedContact && (
        <TaskPanelMobile
          contactId={selectedContact.phone}
          contactName={selectedContact.name}
          contactPhone={selectedContact.phone}
          clinicId={parseInt(localStorage.getItem('clinic_id') || '1')}
          onClose={() => setShowTaskPanel(false)}
        />
      )}
      {correctionMessage && correctionContactId === selectedContactId && correctionClinicId === activeClinicId() && selectedContact && isSelectedContactIdentityResolved && hasPermission('prompt') && correctionClinicId && (
        <AgentCorrectionDialog
          key={`${selectedContactId}:${correctionMessage.id}`}
          clinicId={correctionClinicId}
          message={correctionMessage}
          onClose={() => setCorrectionMessage(null)}
          onPublished={() => { setCorrectionMessage(null); if (correctionClinicId === activeClinicId()) setCorrectionPublished(true); }}
        />
      )}
      {correctionPublished && <div role="status" className={`fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-4 right-4 z-50 flex items-center justify-between gap-3 rounded-xl border border-sminy-primary/20 px-4 py-3 text-sm shadow-xl ${isDark ? 'bg-slate-800 text-white' : 'bg-white text-slate-800'}`}><span>Correção publicada na biblioteca da clínica.</span><button type="button" onClick={() => setCorrectionPublished(false)} className="min-h-11 rounded-xl px-2 text-sminy-primary">Fechar</button></div>}
    </div>
  );
};

export default ChatMobile;
